import { Component, Raw } from 'vue'
import { acceptHMRUpdate, defineStore } from 'pinia'
import { Card, Minion } from '@/shared/model/Card.ts'
import Phaser from 'phaser'
import { Player } from '@/shared/model/Player.ts'
import { AlignmentGuide, LibraryCardUsage, MinionActionType } from '@/shared/types/state.ts'
import { CardGroup, CardInGame, DragAttrs, DragOver, PlayerInGame } from '@/client/game/types.ts'
import { CARD_COST_FX_DURATION, CARD_PING_DURATION } from '@/shared/const/game.ts'
import { selfCanSeeOrPeek } from '@/client/state/self.ts'
import { AnyCardRegion, CardOid, PlayerOid, Point2D } from '@/shared/types/model.ts'
import Rectangle = Phaser.Geom.Rectangle

export enum AlertState {
    Error = 'error',
    Warning = 'warning',
    Success = 'success',
}

export type AlertMessage = {
    message: string
    type: AlertState
    dismissible: boolean
    blockInteraction: boolean
}

let transitionTimer: ReturnType<typeof setTimeout> | null = null
let columnTimer: ReturnType<typeof setTimeout> | null = null

// Normalizes a drag's two opposite corners ( origin, current ) into a
// positive-width/height Rectangle, regardless of drag direction.
function rectFromCorners(origin: Point2D, current: Point2D): Rectangle {
    let { x, y } = origin
    let [width, height] = [current.x - x, current.y - y]

    if (width < 0) {
        x += width
        width *= -1
    }
    if (height < 0) {
        y += height
        height *= -1
    }

    return new Rectangle(x, y, width, height)
}

export const useBusStore = defineStore('bus', {
    state: () => ({
        alert: null as AlertMessage | null,
        isUserProfilePanelOpen: false,
        isDeckPanelOpen: false,
        deckPanelInitialTab: 'history' as string,

        isResyncing: false,
    }),
    actions: {
        alertError(message: string, { dismissible = true, blockInteraction = false } = {}) {
            this.alert = {
                message,
                type: AlertState.Error,
                dismissible,
                blockInteraction,
            }
        },
        alertWarning(message: string, { dismissible = true, blockInteraction = false } = {}) {
            this.alert = {
                message,
                type: AlertState.Warning,
                dismissible,
                blockInteraction,
            }
        },
        alertSuccess(message: string, { dismissible = true, blockInteraction = false } = {}) {
            this.alert = {
                message,
                type: AlertState.Success,
                dismissible,
                blockInteraction,
            }
        },
        dismissAlert() {
            this.alert = null
        },
    },
})

export const useGameBusStore = defineStore('gameBus', {
    state: () => ({
        /** Focus Mode **/
        focusMode: false,
        focusModeTransitioning: false,

        /** Pointer **/
        pointerPosition: null as Point2D | null, // Expressed in world coordinates
        dragAttrs: null as DragAttrs | null,
        hoveredCard: null as Card | null,
        zoomHoveredCard: false,

        /** Close-up card **/
        closeUpCard: {
            card: null as Card | null,
            canView: false,
        },

        pinnedCloseUpCard: {
            card: null as Card | null,
            canView: false,
        },

        // Incremented to trigger a transient pin indicator on the close-up card
        closeUpCardPinFlash: 0,

        /** Game Objects **/
        cardsInGame: {} as Record<CardOid, CardInGame>,
        playersInGame: {} as Record<PlayerOid, PlayerInGame>,

        /** Card selection **/
        selectedCards: [] as Card[],
        selectionArea: {
            show: false,
            origin: null as Point2D | null, // Expressed in world coordinates
            // Screen-coordinate counterparts of origin/pointerPosition, for the
            // visual rectangle only ( selectionAreaScreenRect ) : Free Table's
            // per-player camera rotation would otherwise render it tilted.
            originScreen: null as Point2D | null,
            currentScreen: null as Point2D | null,
        },

        /** Card groups **/
        cardGroups: [] as CardGroup[],
        cardGroupCandidate: null as CardGroup | null,
        cardPendingIntoGroup: null as Card | null,

        /** Action declaration by drag **/
        // Minion highlighted while dragging a playable card from hand over it
        dropMinionCandidate: null as Minion | null,

        /** Alignment guides **/
        dragOver: null as DragOver | null,
        alignmentGuides: [] as AlignmentGuide[],

        /** Target declaration **/
        declaringTargetOrigin: null as Card | null,

        /** Action declaration **/
        actionDeclaration: {
            type: null as MinionActionType | null,
            actingMinion: null as Minion | null,
            // Usage for action cards
            usage: null as LibraryCardUsage | null,
            // Allowed targets for target declaration
            validTargets: null as Card[] | null,
            // Allowed action cards for an action card declaration ( from hand or in play )
            validActionCards: null as Card[] | null,
        },

        /** Card ping **/
        pingedCards: [] as CardOid[],

        /** Floating cost indicator ( automatic cost payment ) **/
        // Per card, the cost to briefly display above it. `seq` forces a fresh
        // animation when the same card is played again.
        cardCosts: {} as Record<CardOid, { blood: number; pool: number; seq: number }>,
        cardCostSeq: 0,

        /** Hand **/
        handDropGapPosition: null as null | number,
        draggedHandCardPosition: null as null | number,

        /** Wield Card Stack **/
        stackDropGapPosition: null as null | number,
        draggedStackCardPosition: null as null | number,

        /** Menus **/
        contextMenu: {
            show: false,
            cards: [] as Card[],
            x: 0,
            y: 0,
            fromStackRegion: false,
            submenu: {
                show: false,
                component: null as Raw<Component> | null,
                x: 0,
                y: 0,
            },
        },
        changePool: {
            show: false,
            player: null as Player | null,
        },
        secretInterfaceShown: false,
        wieldCardStack: {
            show: false,
            cardRegion: null as AnyCardRegion | null,
            searchString: '',
        },
    }),

    getters: {
        /** Card selection **/
        selectionAreaRect(state) {
            if (
                !state.selectionArea.show ||
                !state.pointerPosition ||
                !state.selectionArea.origin
            ) {
                return null
            }

            return rectFromCorners(state.selectionArea.origin, state.pointerPosition)
        },
        // Screen-space counterpart of selectionAreaRect, for the visual
        // rectangle in Free Table ( see SelectionArea.vue ).
        selectionAreaScreenRect(state) {
            if (
                !state.selectionArea.show ||
                !state.selectionArea.currentScreen ||
                !state.selectionArea.originScreen
            ) {
                return null
            }

            return rectFromCorners(
                state.selectionArea.originScreen,
                state.selectionArea.currentScreen,
            )
        },
        selectedCardsInGame(state): CardInGame[] {
            return state.selectedCards.map(c => state.cardsInGame[c.oid]).filter(c => c)
        },

        /** Card groups **/
        cardGroupsByCard(state): Record<CardOid, CardGroup> {
            const result: Record<CardOid, CardGroup> = {}
            for (const group of state.cardGroups) {
                for (const cardOid of group) {
                    result[cardOid] = group
                }
            }
            return result
        },
        // Indirect hover: cards that are not directly hovere, but are part of a group of the hovered card
        indirectHoveredCards(state): CardOid[] {
            if (state.hoveredCard) {
                return [...(this.cardGroupsByCard[state.hoveredCard.oid] ?? [])].filter(
                    cardOid => cardOid != state.hoveredCard?.oid,
                )
            }
            return []
        },

        // Indirect selection: cards that are not directly selected, but are part of a group of selected cards
        indirectSelectedCards(state): CardOid[] {
            const result = new Set<CardOid>()
            const selectedCardsOid = state.selectedCards.map(c => c.oid)
            for (const selectedCard of state.selectedCards) {
                for (const cardOid of this.cardGroupsByCard[selectedCard.oid] ?? []) {
                    if (!selectedCardsOid.includes(cardOid)) {
                        result.add(cardOid)
                    }
                }
            }
            return [...result]
        },
        indirectSelectedCardsInGame(state): CardInGame[] {
            return this.indirectSelectedCards
                .map(cardOid => state.cardsInGame[cardOid])
                .filter(c => c)
        },
    },

    actions: {
        setCloseUpCard(
            card: Card | null,
            {
                canView,
                pinned,
                flash,
            }: {
                canView?: boolean
                pinned?: boolean
                flash?: boolean
            } = {},
        ) {
            if (canView === undefined) {
                canView = card ? selfCanSeeOrPeek(card) : false
            }
            this.closeUpCard = { card, canView }
            if (pinned) {
                this.pinnedCloseUpCard = { card, canView }
                if (flash) {
                    this.closeUpCardPinFlash++
                }
            }
        },

        assignPinnedCloseUpCard() {
            this.closeUpCard = this.pinnedCloseUpCard
        },

        hideContextSubmenu() {
            this.contextMenu.submenu.show = false
            this.contextMenu.submenu.component = null
        },

        hideContextMenu() {
            this.contextMenu.show = false
            this.hideContextSubmenu()
        },

        isCardSelected(card: Card): boolean {
            return this.selectedCards.includes(card)
        },
        removeFromSelection(card: Card) {
            this.selectedCards = this.selectedCards.filter(c => c.oid != card.oid)
        },
        removeFromCardGroup(card: Card) {
            const cardGroup = this.cardGroupsByCard[card.oid]
            if (cardGroup) {
                // If there's only 2 cards left, delete the card group entirely
                if (cardGroup.size == 2) {
                    this.removeCardGroup(cardGroup)
                } else {
                    cardGroup.delete(card.oid)
                }
            }
        },
        removeCardGroup(cardGroup: CardGroup) {
            this.cardGroups = this.cardGroups.filter(cg => cg != cardGroup)
        },

        pingCard(cardOid: CardOid) {
            this.pingedCards.push(cardOid)
            // Remove the ping after CARD_PING_DURATION milliseconds
            setTimeout(() => {
                this.pingedCards = this.pingedCards.filter(c => c != cardOid)
            }, CARD_PING_DURATION * 2)
        },

        // Show a floating cost indicator above a card. Only the acting player
        // calls this ; it is a purely local visual, not a broadcast mutation.
        showCardCost(cardOid: CardOid, blood: number, pool: number) {
            if (blood <= 0 && pool <= 0) {
                return
            }
            const seq = ++this.cardCostSeq
            this.cardCosts[cardOid] = { blood, pool, seq }
            // Remove the indicator once the animation is over.
            setTimeout(() => {
                if (this.cardCosts[cardOid]?.seq == seq) {
                    delete this.cardCosts[cardOid]
                }
            }, CARD_COST_FX_DURATION)
        },

        toggleFocusMode() {
            if (transitionTimer) {
                clearTimeout(transitionTimer)
            }
            if (columnTimer) {
                clearTimeout(columnTimer)
            }
            const newFocusMode = !this.focusMode
            this.focusModeTransitioning = true
            transitionTimer = setTimeout(() => {
                this.focusModeTransitioning = false
                transitionTimer = null
            }, 600)
            columnTimer = setTimeout(() => {
                this.focusMode = newFocusMode
                columnTimer = null
            }, 250)
        },
    },
})

if (import.meta.hot) {
    import.meta.hot.accept(acceptHMRUpdate(useBusStore, import.meta.hot))
}
